/**
 * Frontline deck / mana / hand helpers (Phaser-free).
 */

import { frontlineCatalog } from "../../data/frontline-catalog";

const SK = frontlineCatalog.skills;
const ECO = SK.economy;

export type FlSkillId =
  | "legion"
  | "horde"
  | "champion"
  | "titan"
  | "mend"
  | "aegis"
  | "smite"
  | "elevate"
  | "stun"
  | "surge"
  | "cataclysm";

export type TeamCards = {
  mana: number;
  deck: FlSkillId[];
  hand: FlSkillId[];
  discard: FlSkillId[];
  /** Exile (소멸) — never returns to deck/hand. */
  exiled: FlSkillId[];
  drawCd: number;
  aiCastCd: number;
};

export function isFlSkillId(id: string): id is FlSkillId {
  return (SK.order as string[]).includes(id);
}

export function cardMana(id: FlSkillId): number {
  return SK.cards[id].mana;
}

export function cardVfx(id: FlSkillId): string {
  return SK.cards[id].vfx;
}

export function cardIsExile(id: FlSkillId): boolean {
  return Boolean((SK.cards[id] as { exile?: boolean }).exile);
}

type Rng = { next: () => number };

function shuffleInPlace(arr: FlSkillId[], rng: Rng): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1)) % (i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

export function createTeamCards(rng: Rng): TeamCards {
  const deck = [...(SK.starterDeck as FlSkillId[])];
  shuffleInPlace(deck, rng);
  const hand: FlSkillId[] = [];
  while (hand.length < ECO.handSize && deck.length > 0) {
    hand.push(deck.pop()!);
  }
  return {
    mana: ECO.startMana,
    deck,
    hand,
    discard: [],
    exiled: [],
    drawCd: ECO.drawIntervalSec,
    aiCastCd: SK.ai.castIntervalSec * 0.4,
  };
}

export function ensureDeck(team: TeamCards, rng: Rng): void {
  if (team.deck.length > 0) return;
  if (team.discard.length === 0) return;
  team.deck = team.discard.splice(0, team.discard.length);
  shuffleInPlace(team.deck, rng);
}

export function tryDrawOne(team: TeamCards, rng: Rng): boolean {
  if (team.hand.length >= ECO.handSize) return false;
  ensureDeck(team, rng);
  if (team.deck.length === 0) return false;
  team.hand.push(team.deck.pop()!);
  return true;
}

export function tickManaAndDraw(team: TeamCards, dt: number, rng: Rng): void {
  team.mana = Math.min(ECO.maxMana, team.mana + ECO.manaRegen * dt);
  team.drawCd -= dt;
  if (team.drawCd > 0) return;
  team.drawCd = ECO.drawIntervalSec;
  tryDrawOne(team, rng);
}

export function handHas(team: TeamCards, id: FlSkillId): boolean {
  return team.hand.includes(id);
}

/**
 * Spend mana and remove one copy from hand.
 * Normal → discard (reshuffle pool). Exile(소멸) → exiled (never returns).
 */
export function spendCard(
  team: TeamCards,
  id: FlSkillId,
  handIndex?: number,
): { ok: true; exile: boolean } | { ok: false; reason: "no_mana" | "not_in_hand" } {
  const idx =
    handIndex != null && team.hand[handIndex] === id
      ? handIndex
      : team.hand.indexOf(id);
  if (idx < 0) return { ok: false, reason: "not_in_hand" };
  const cost = cardMana(id);
  if (team.mana < cost) return { ok: false, reason: "no_mana" };
  team.mana -= cost;
  team.hand.splice(idx, 1);
  const exile = cardIsExile(id);
  if (exile) team.exiled.push(id);
  else team.discard.push(id);
  return { ok: true, exile };
}

/** Undo a spend after a failed cast (before effect applied). */
export function refundCard(
  team: TeamCards,
  id: FlSkillId,
  exile: boolean,
): void {
  team.mana = Math.min(ECO.maxMana, team.mana + cardMana(id));
  if (exile) {
    const ei = team.exiled.lastIndexOf(id);
    if (ei >= 0) team.exiled.splice(ei, 1);
  } else {
    const di = team.discard.lastIndexOf(id);
    if (di >= 0) team.discard.splice(di, 1);
  }
  team.hand.push(id);
}

export function pickAiCard(team: TeamCards): FlSkillId | null {
  for (const id of SK.ai.preferOrder as FlSkillId[]) {
    if (!handHas(team, id)) continue;
    if (team.mana < cardMana(id)) continue;
    return id;
  }
  return null;
}
